import React from 'react';
import { useIntl } from 'react-intl';

import useQueryParams from '@/hooks/useQueryParams';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';
import { isDeliveryModeFromParams } from '@/utility/queryParams';

import globalCss from '../DesktopUniversalSearch/DesktopUniversalSearch.module.css';
import { SEARCH_TAB } from './DesktopUniversalSearch';
import classes from './LocationSearchInput.module.css';

interface ILocationSearchInputProps {
  getInputProps: () => Record<string, never>;
  className?: string;
  isTowables: boolean;
  selectedTab: SEARCH_TAB | null;
  isCategoryEnabled?: boolean;
  isStayFilterEnabled?: boolean;
}

const LocationSearchInput: React.FC<ILocationSearchInputProps> = ({
  getInputProps,
  className = '',
  isTowables,
  selectedTab,
  isCategoryEnabled,
  isStayFilterEnabled,
}) => {
  const intl = useIntl();
  const queryParams = useQueryParams();
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );

  const isDeliveryMode = isDeliveryModeFromParams(queryParams);

  const getLocationPlaceholder = () => {
    if (!isRentalsVDeliverySearchModeEnabled) {
      return intl.formatMessage({
        defaultMessage: 'Location',
        id: 'cGCkb9',
        description: 'Global Header > Placeholder text for header search field',
      });
    }

    return isDeliveryMode
      ? intl.formatMessage({
          defaultMessage: 'Search a delivery location',
          id: 'HBXYrQ',
          description: 'Location placeholder for delivery mode',
        })
      : intl.formatMessage({
          defaultMessage: 'Pickup location',
          id: '798AXp',
          description: 'Location placeholder for RV rental mode',
        });
  };

  const getNoValuePlaceholder = () => {
    if (!isRentalsVDeliverySearchModeEnabled) {
      return intl.formatMessage({
        defaultMessage: 'Where will your adventure start?',
        id: 'S/dTIs',
      });
    }

    return isDeliveryMode
      ? intl.formatMessage({
          defaultMessage: 'Add a delivery location',
          id: 'nK3TK0',
        })
      : intl.formatMessage({
          defaultMessage: 'Add a pickup location',
          id: 'nrVolc',
        });
  };

  const locationPlaceholder = getLocationPlaceholder();

  const homeLocationPlaceholder = intl.formatMessage({
    defaultMessage: 'Type to search',
    id: '0bIwFw',
    description: 'Global Header > Placeholder text for header search field',
  });

  const noValuePlaceholder = getNoValuePlaceholder();

  // For other tags
  const { 'aria-controls': ariaControls, value } = getInputProps();
  // For input
  const { onFocus, ...inputProps } = getInputProps() as { onFocus?: () => void };

  return (
    <div
      className={`${className} ${
        isTowables || selectedTab === SEARCH_TAB.LOCATION
          ? isStayFilterEnabled
            ? 'rounded-tr-2xl'
            : 'rounded-none'
          : 'lg:rounded-2xl'
      } ${isTowables ? classes.towableLocationTab : classes.locationTab} ${
        !isCategoryEnabled ? 'rounded-tl-2xl rounded-tr-2xl lg:rounded-tr-none' : ''
      } ${isStayFilterEnabled ? 'rounded-t-2xl' : ''}`}>
      <div className={classes.homeInputContainer} data-focus={!!ariaControls}>
        <span className={globalCss.homeLabel}>{locationPlaceholder}</span>
        <input
          className={classes.homeInput}
          placeholder={homeLocationPlaceholder}
          onFocus={e => {
            onFocus?.();
            e.target.select();
          }}
          {...inputProps}
        />
        {!value && <span>{noValuePlaceholder}</span>}
      </div>
    </div>
  );
};

export default LocationSearchInput;
